import { isValidDate } from '@buildwise/libs/helpers';
import { CustomFieldValueType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { ConfigEntryFieldType, ConfigEntryType } from 'config/config';
import * as Yup from 'yup';

export const getCustomFieldsValidationSchema = ({
  doNotAddRequiredValidation = false,
  fields,
  getPredefinedFieldValidation,
}: {
  doNotAddRequiredValidation?: boolean;
  fields: ConfigEntryType[];
  getPredefinedFieldValidation?: (options: { field: ConfigEntryType }) => any;
}) => {
  const schema: Record<string, any> = {};

  fields.forEach((field) => {
    let fieldSchema: any = Yup.array(Yup.string()).nullable();

    switch (field.referenceObject?.type || ConfigEntryFieldType.MULTI_SELECT) {
      case ConfigEntryFieldType.MULTI_SELECT:
      case ConfigEntryFieldType.ATTACHMENTS:
      case ConfigEntryFieldType.CHECKBOX:
      case ConfigEntryFieldType.REGEX: {
        fieldSchema = Yup.array(Yup.string()).nullable();
        if (field.referenceObject?.isRequired && !doNotAddRequiredValidation) {
          fieldSchema = fieldSchema.min(1, 'This is required');
        }
        break;
      }

      case ConfigEntryFieldType.SELECT:
      case ConfigEntryFieldType.EDITOR:
      case ConfigEntryFieldType.RADIO:
      case ConfigEntryFieldType.RATING:
      case ConfigEntryFieldType.TEXT:
      case ConfigEntryFieldType.TEXTAREA: {
        fieldSchema = Yup.string();
        break;
      }

      case ConfigEntryFieldType.NUMBER: {
        fieldSchema = Yup.number();
        break;
      }

      case ConfigEntryFieldType.DATE: {
        fieldSchema = Yup.date().nullable();
        break;
      }

      default: {
        fieldSchema = Yup.array(Yup.string()).nullable();
        if (field.referenceObject?.isRequired && !doNotAddRequiredValidation) {
          fieldSchema = fieldSchema.min(1, 'This is required');
        }
      }
    }

    // Check if the field is required
    if (field.referenceObject?.isRequired && !doNotAddRequiredValidation) {
      fieldSchema = fieldSchema.required('Required field');
    }

    schema[field.entryCode] = getPredefinedFieldValidation?.({ field }) || fieldSchema;
  });

  return schema;
};

export const getCustomFieldsDefaultValues = ({
  data,
  fields,
  getPredefinedFieldDefaultValue,
}: {
  data?: Record<string, any>;
  fields: ConfigEntryType[];
  getPredefinedFieldDefaultValue?: (options: { field: ConfigEntryType }) => any;
}) => {
  const defaultValues: Record<string, any> = {};
  fields.forEach((field) => {
    let defaultValue =
      data?.[field.entryCode] ||
      (data?.customFields?.[field.entryCode] as CustomFieldValueType)?.fieldValue;
    defaultValue = getFieldDefaultValue({
      defaultValue,
      field,
    });

    defaultValues[field.entryCode] = getPredefinedFieldDefaultValue?.({ field }) ?? defaultValue;
  });

  return defaultValues;
};

export const getFieldDefaultValue = ({
  defaultValue: value,
  field,
}: {
  defaultValue?: any;
  field: ConfigEntryType;
}) => {
  let defaultValue = value;
  switch (field.referenceObject?.type || ConfigEntryFieldType.MULTI_SELECT) {
    case ConfigEntryFieldType.MULTI_SELECT:
    case ConfigEntryFieldType.ATTACHMENTS:
    case ConfigEntryFieldType.CHECKBOX:
    case ConfigEntryFieldType.REGEX: {
      if (!Array.isArray(defaultValue)) {
        defaultValue = defaultValue ? [defaultValue] : [];
      }
      return defaultValue;
    }

    case ConfigEntryFieldType.SELECT:
    case ConfigEntryFieldType.EDITOR:
    case ConfigEntryFieldType.RADIO:
    case ConfigEntryFieldType.RATING:
    case ConfigEntryFieldType.TEXT:
    case ConfigEntryFieldType.TEXTAREA: {
      if (Array.isArray(defaultValue)) {
        defaultValue = defaultValue?.[0] || '';
      }

      if (typeof defaultValue === 'number') {
        defaultValue = String(defaultValue);
      }

      if (!defaultValue) {
        defaultValue = '';
      }

      return defaultValue;
    }

    case ConfigEntryFieldType.NUMBER: {
      if (Array.isArray(defaultValue)) {
        defaultValue = defaultValue?.[0];
      }

      if (typeof defaultValue === 'string') {
        defaultValue = Number(defaultValue) || undefined;
      }
      return defaultValue;
    }

    case ConfigEntryFieldType.DATE: {
      if (Array.isArray(defaultValue)) {
        defaultValue = defaultValue?.[0];
      }

      defaultValue = isValidDate(defaultValue) ? defaultValue : undefined;
      return defaultValue;
    }

    default: {
      if (!Array.isArray(defaultValue)) {
        defaultValue = defaultValue ? [defaultValue] : [];
      }

      return defaultValue;
    }
  }
};
